import { ref, deleteObject } from 'firebase/storage';
import { doc, setDoc, getDoc, updateDoc, deleteField } from 'firebase/firestore';
import { storage, db } from '../config/firebase';
import { User, getAuth } from 'firebase/auth';

// Collection name for user profiles
const USER_PROFILES_COLLECTION = 'users';  // Changed to match backend

/**
 * Interface for user profile data
 */
export interface UserProfile {
  email: string;
  profilePicture?: string;  // Changed to match backend field name
  photoURL?: string;  // Keep for backward compatibility
  displayName?: string;
  lastUpdated?: number;
  updatedAt?: any;  // Firebase timestamp from backend
}

/**
 * Uploads a profile picture using Netlify function to avoid CORS issues
 * @param user The current user
 * @param file The image file to upload
 * @returns Promise with the download URL
 */
export const uploadProfilePicture = async (user: User, file: File): Promise<string> => {
  if (!user || !user.uid) {
    throw new Error('User not authenticated');
  }

  try {
    // Validate file type
    if (!file.type.startsWith('image/')) {
      throw new Error('File must be an image');
    }

    // Get the ID token for authentication
    const idToken = await user.getIdToken();
    
    // Create form data
    const formData = new FormData();
    formData.append('file', file);
    
    // Upload using Netlify function
    const response = await fetch('/.netlify/functions/uploadProfilePicture', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`
      },
      body: formData
    });
    
    let errorMessage = 'Failed to upload profile picture';
    
    if (!response.ok) {
      try {
        const errorData = await response.json();
        errorMessage = errorData.error || errorMessage;
      } catch (parseError) {
        
        // If we can't parse the response, use the status text
        errorMessage = `${errorMessage}: ${response.status} ${response.statusText}`;
      }
      throw new Error(errorMessage);
    }
    
    let data;
    try {
      data = await response.json();
    } catch (parseError) {
      
      throw new Error('Invalid response from server');
    }
    
    if (!data || !data.url) {
      throw new Error('No URL returned from server');
    }
    
    const downloadURL = data.url;
    
    // Update the user profile in Firestore (the function already does this, but we'll do it here too for consistency)
    await updateUserProfile(user.uid, user.email || '', { profilePicture: downloadURL });
    
    return downloadURL;
  } catch (error) {
    
    throw error;
  }
};

/**
 * Deletes a user's profile picture using Netlify function to avoid CORS issues
 * @param userId The user ID
 * @returns Promise that resolves when the picture is deleted
 */
export const deleteProfilePicture = async (userId: string): Promise<void> => {
  try {
    // Get the current user
    const auth = getAuth();
    const currentUser = auth.currentUser;
    
    if (!currentUser) {
      throw new Error('User not authenticated');
    }
    
    // Get the ID token for authentication
    const idToken = await currentUser.getIdToken();
    
    // Delete using Netlify function
    const response = await fetch('/.netlify/functions/deleteProfilePicture', {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${idToken}`
      }
    });
    
    let errorMessage = 'Failed to delete profile picture';
    
    if (!response.ok) {
      try {
        const errorData = await response.json();
        errorMessage = errorData.error || errorMessage;
      } catch (parseError) {
        
        // If we can't parse the response, use the status text
        errorMessage = `${errorMessage}: ${response.status} ${response.statusText}`;
      }
      throw new Error(errorMessage);
    }
    
    // Update the user profile in Firestore (the function already does this, but we'll do it here too for consistency)
    const userProfile = await getUserProfile(userId);
    if (userProfile) {
      // Use deleteField() instead of undefined to properly remove the field
      const docRef = doc(db, USER_PROFILES_COLLECTION, userId);
      await updateDoc(docRef, {
        profilePicture: deleteField(),
        photoURL: deleteField(),  // Remove both for backward compatibility
        updatedAt: Date.now()
      });
    }
  } catch (error) {
    
    throw error;
  }
};

/**
 * Gets a user's profile from Firestore
 * @param userId The user ID
 * @returns Promise with the user profile
 */
export const getUserProfile = async (userId: string): Promise<UserProfile | null> => {
  try {
    const docRef = doc(db, USER_PROFILES_COLLECTION, userId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return docSnap.data() as UserProfile;
    }
    
    return null;
  } catch (error) {
    
    throw error;
  }
};

/**
 * Updates a user's profile in Firestore
 * @param userId The user ID
 * @param email The user's email
 * @param updates The profile updates
 * @returns Promise that resolves when the profile is updated
 */
export const updateUserProfile = async (
  userId: string, 
  email: string, 
  updates: Partial<UserProfile>
): Promise<void> => {
  try {
    const docRef = doc(db, USER_PROFILES_COLLECTION, userId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      // Update existing document
      await updateDoc(docRef, {
        ...updates,
        updatedAt: Date.now()
      });
    } else {
      // Create new document
      await setDoc(docRef, {
        email,
        ...updates,
        updatedAt: Date.now()
      });
    }
  } catch (error) {
    
    throw error;
  }
};

/**
 * Resizes an image to the specified dimensions
 * @param file The image file
 * @param maxWidth The maximum width
 * @param maxHeight The maximum height
 * @returns Promise with the resized image as a Blob
 */
const resizeImage = async (file: File, maxWidth: number, maxHeight: number): Promise<Blob> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.src = URL.createObjectURL(file);
    
    img.onload = () => {
      // Calculate new dimensions while maintaining aspect ratio
      let width = img.width;
      let height = img.height;
      
      if (width > height) {
        if (width > maxWidth) {
          height = Math.round(height * (maxWidth / width));
          width = maxWidth;
        }
      } else {
        if (height > maxHeight) {
          width = Math.round(width * (maxHeight / height));
          height = maxHeight;
        }
      }
      
      // Create canvas and draw resized image
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Could not get canvas context'));
        return;
      }
      
      ctx.drawImage(img, 0, 0, width, height);
      
      // Convert canvas to blob
      canvas.toBlob((blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Could not convert canvas to blob'));
        }
      }, file.type);
    };
    
    img.onerror = () => {
      reject(new Error('Error loading image'));
    };
  });
};
